import { useState, useEffect, useCallback, useRef } from 'react';
import { agedStockApi } from '../services/agedStockApi';
import type {
    AgedStockItem,
    AgedStockFilterOptions,
    AgedStockFilters,
    AgedStockStats,
    AgedStockFlagStats,
    AgedStockSortField,
    AgedStockSortOrder,
} from '../types/agedStock';

interface UseAgedStockReturn {
    items: AgedStockItem[];
    total: number;
    loading: boolean;
    error: string | null;
    stats: AgedStockStats | null;
    flagStats: AgedStockFlagStats | null;
    filters: AgedStockFilters;
    filterOptions: AgedStockFilterOptions | null;
    sortBy: AgedStockSortField;
    sortOrder: AgedStockSortOrder;
    page: number;
    limit: number;
    setFilters: (filters: AgedStockFilters) => void;
    setSort: (field: AgedStockSortField) => void;
    setPage: (page: number) => void;
    refresh: () => void;
}

const SESSION_STORAGE_KEY = 'aged-stock-state';

interface PersistedState {
    filters: AgedStockFilters;
    sortBy: AgedStockSortField;
    sortOrder: AgedStockSortOrder;
}

// Default: show all real lots, hide synthetic '(unaccounted)' pseudo-lots
// (user can uncheck "Only with lot" to see total including pseudo-lots).
const DEFAULT_FILTERS: AgedStockFilters = { onlyWithLot: true };
const DEFAULT_SORT_BY: AgedStockSortField = 'firstPurchaseDate';
const DEFAULT_SORT_ORDER: AgedStockSortOrder = 'ASC';

function loadPersistedState(): PersistedState {
    if (typeof window === 'undefined') {
        return { filters: DEFAULT_FILTERS, sortBy: DEFAULT_SORT_BY, sortOrder: DEFAULT_SORT_ORDER };
    }
    try {
        const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!raw) return { filters: DEFAULT_FILTERS, sortBy: DEFAULT_SORT_BY, sortOrder: DEFAULT_SORT_ORDER };
        const parsed = JSON.parse(raw) as Partial<PersistedState>;
        return {
            filters: parsed.filters ?? DEFAULT_FILTERS,
            sortBy: parsed.sortBy ?? DEFAULT_SORT_BY,
            sortOrder: parsed.sortOrder ?? DEFAULT_SORT_ORDER,
        };
    } catch {
        return { filters: DEFAULT_FILTERS, sortBy: DEFAULT_SORT_BY, sortOrder: DEFAULT_SORT_ORDER };
    }
}

export function useAgedStock(): UseAgedStockReturn {
    const [items, setItems] = useState<AgedStockItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<AgedStockStats | null>(null);
    const [flagStats, setFlagStats] = useState<AgedStockFlagStats | null>(null);

    const initial = loadPersistedState();
    const [filters, setFiltersState] = useState<AgedStockFilters>(initial.filters);
    const [filterOptions, setFilterOptions] = useState<AgedStockFilterOptions | null>(null);
    const [sortBy, setSortBy] = useState<AgedStockSortField>(initial.sortBy);
    const [sortOrder, setSortOrder] = useState<AgedStockSortOrder>(initial.sortOrder);
    const [page, setPage] = useState(1);
    const limit = 50;
    const refreshCounter = useRef(0);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const payload: PersistedState = { filters, sortBy, sortOrder };
            window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
        } catch {
            // ignore storage errors
        }
    }, [filters, sortBy, sortOrder]);

    useEffect(() => {
        agedStockApi.getFilters(filters)
            .then(setFilterOptions)
            .catch(err => console.error('Failed to load aged-stock filter options:', err));
    }, [filters.companyCode, filters.businessUnit, filters.parentCategory, filters.warehouseCountry]);

    useEffect(() => {
        let cancelled = false;
        const currentRefresh = ++refreshCounter.current;

        setLoading(true);
        setError(null);

        Promise.all([
            agedStockApi.list({ page, limit, sortBy, sortOrder, filters }),
            agedStockApi.getStats(filters),
            agedStockApi.getFlagStats(),
        ])
            .then(([listResult, statsResult, flagStatsResult]) => {
                if (!cancelled && currentRefresh === refreshCounter.current) {
                    setItems(listResult.items);
                    setTotal(listResult.total);
                    setStats(statsResult);
                    setFlagStats(flagStatsResult);
                }
            })
            .catch(err => {
                if (!cancelled && currentRefresh === refreshCounter.current) {
                    setError(err instanceof Error ? err.message : 'Unknown error');
                }
            })
            .finally(() => {
                if (!cancelled && currentRefresh === refreshCounter.current) {
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [filters, sortBy, sortOrder, page]);

    const setFilters = useCallback((newFilters: AgedStockFilters) => {
        setFiltersState(newFilters);
        setPage(1);
    }, []);

    const setSort = useCallback((field: AgedStockSortField) => {
        setSortBy(prev => {
            if (prev === field) {
                setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
            } else {
                setSortOrder('ASC');
            }
            return field;
        });
        setPage(1);
    }, []);

    const refresh = useCallback(() => {
        refreshCounter.current++;
        setLoading(true);
        setError(null);

        Promise.all([
            agedStockApi.list({ page, limit, sortBy, sortOrder, filters }),
            agedStockApi.getStats(filters),
            agedStockApi.getFlagStats(),
        ])
            .then(([listResult, statsResult, flagStatsResult]) => {
                setItems(listResult.items);
                setTotal(listResult.total);
                setStats(statsResult);
                setFlagStats(flagStatsResult);
            })
            .catch(err => setError(err instanceof Error ? err.message : 'Unknown error'))
            .finally(() => setLoading(false));
    }, [page, sortBy, sortOrder, filters]);

    return {
        items, total, loading, error, stats, flagStats,
        filters, filterOptions,
        sortBy, sortOrder, page, limit,
        setFilters, setSort, setPage, refresh,
    };
}
